import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { api } from '../../lib/api.js'
import { useHatcheryScope } from '../../lib/scope.js'
import {
  Card, ErrorBox, Field, Page, Select, Spinner, SubmitButton, useAsync, useConfirm, useToast
} from '../../components/Ui.jsx'
import { BROODSTOCK, OFFER_TYPES, PL_STAGES, addDays, inr, todayISO } from '../../lib/format.js'

const BLANK = {
  species: 'L. vannamei',
  broodstockSource: 'Kona Bay (Hawaii)',
  caaLicenseNo: '',
  totalCapacityLakh: '',
  minOrderLakh: 2,
  readyDate: addDays(todayISO(), 7),
  plStage: 'PL12',
  salinityPpt: '',
  stressTestPct: '',
  feedProtocol: '',
  sizeCvPct: '',
  pcrLabName: '',
  pcrScreenDate: todayISO(),
  pcrWssv: 'negative',
  pcrEhp: 'negative',
  pcrIhhnv: 'negative',
  basePricePer1000: '',
  offerType: 'none',
  offerValue: '',
  offerMinQtyLakh: '',
  offerDetails: '',
  offerStart: '',
  offerEnd: '',
  promoNote: ''
}

const RESULTS = ['negative', 'positive', 'not tested']

export default function BatchForm () {
  const { id } = useParams()
  const editing = Boolean(id)
  const scope = useHatcheryScope()
  const navigate = useNavigate()
  const toast = useToast()
  const confirm = useConfirm()

  const [form, setForm] = useState(BLANK)
  const [busy, setBusy] = useState(false)
  const [pcrFile, setPcrFile] = useState(null)
  const [mediaFiles, setMediaFiles] = useState([])
  const pcrRef = useRef(null)
  const mediaRef = useRef(null)

  const existing = useAsync(
    () => (editing ? api.get(`/hatchery/batches/${id}`) : Promise.resolve(null)),
    [id]
  )

  useEffect(() => {
    const b = existing.data?.batch
    if (!b) return
    setForm({
      species: b.species,
      broodstockSource: b.broodstockSource || '',
      caaLicenseNo: b.caaLicenseNo || '',
      totalCapacityLakh: b.totalCapacityLakh,
      minOrderLakh: b.minOrderLakh,
      readyDate: b.readyDate,
      plStage: b.plStage,
      salinityPpt: b.salinityPpt ?? '',
      stressTestPct: b.stressTestPct ?? '',
      feedProtocol: b.feedProtocol || '',
      sizeCvPct: b.sizeCvPct ?? '',
      pcrLabName: b.pcr?.labName || '',
      pcrScreenDate: b.pcr?.screenDate || '',
      pcrWssv: b.pcr?.wssv || 'negative',
      pcrEhp: b.pcr?.ehp || 'negative',
      pcrIhhnv: b.pcr?.ihhnv || 'negative',
      basePricePer1000: b.basePricePer1000,
      offerType: b.offer?.type || 'none',
      offerValue: b.offer?.value ?? '',
      offerMinQtyLakh: b.offer?.minQtyLakh ?? '',
      offerDetails: b.offer?.details || '',
      offerStart: b.offer?.start || '',
      offerEnd: b.offer?.end || '',
      promoNote: b.promoNote || ''
    })
  }, [existing.data])

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const submit = async e => {
    e.preventDefault()
    const ok = await confirm({
      title: editing ? 'Save these batch details?' : 'Create this batch?',
      body: editing
        ? 'The desk sees the new numbers immediately, and so do farmers if this batch is published.'
        : 'It is saved as a draft. Nothing is bookable and no farmer sees it until you publish it.',
      facts: [
        ['Stage', form.plStage || 'not set'],
        ['Ready date', form.readyDate || 'not set'],
        ['Capacity', form.totalCapacityLakh ? `${form.totalCapacityLakh} lakh PL` : 'not set'],
        ['Price', form.basePricePer1000 ? `₹${form.basePricePer1000} / 1,000` : 'not set'],
        (pcrFile || mediaFiles.length) && ['Uploading', [pcrFile && 'PCR report', mediaFiles.length && `${mediaFiles.length} photo(s)`].filter(Boolean).join(', ')]
      ],
      confirmLabel: editing ? 'Save changes' : 'Create batch'
    })
    if (!ok) return
    setBusy(true)
    try {
      const body = { ...form, ...scope.body }
      const saved = editing
        ? await api.patch(`/hatchery/batches/${id}`, body)
        : await api.post('/hatchery/batches', body)
      const batchId = saved.batch._id

      if (pcrFile) {
        const fd = new FormData()
        fd.append('report', pcrFile)
        if (form.pcrLabName) fd.append('labName', form.pcrLabName)
        if (form.pcrScreenDate) fd.append('screenDate', form.pcrScreenDate)
        await api.upload(`/hatchery/batches/${batchId}/pcr`, fd)
      }
      if (mediaFiles.length) {
        const fd = new FormData()
        for (const f of mediaFiles) fd.append('files', f)
        await api.upload(`/hatchery/batches/${batchId}/media`, fd)
      }

      toast.ok(editing ? 'Batch updated.' : `Batch ${saved.batch.batchCode} saved as a draft. Publish it to open it for booking.`)
      navigate(`/hatchery/batches/${batchId}${scope.link}`)
    } catch (err) {
      toast.error(err.message)
    } finally {
      setBusy(false)
    }
  }

  if (editing && existing.loading) return <Page title="Edit batch"><Spinner /></Page>
  if (editing && existing.error) return <Page title="Edit batch"><ErrorBox error={existing.error} onRetry={existing.reload} /></Page>

  const qtyThousands = Number(form.totalCapacityLakh || 0) * 100
  const batchValue = qtyThousands * Number(form.basePricePer1000 || 0)

  return (
    <Page
      crumb={<Link to={`/hatchery${scope.link}`}>← My batches</Link>}
      title={editing ? `Edit ${existing.data?.batch?.batchCode}` : 'List a production batch'}
      subtitle="What you enter here is what the operator desk sees when a farmer calls. Pricing, offers and media are internal to the desk — they are never broadcast to farmers."
    >
      <form onSubmit={submit}>
        <Card title="1 · Batch identification & origin">
          <div className="formgrid">
            <Field label="Species" required>
              <Select options={['L. vannamei', 'P. monodon']} value={form.species} onChange={v => set('species', v)} />
            </Field>
            <Field label="Broodstock source" required hint="Farmers ask for this by name.">
              <Select options={BROODSTOCK} value={form.broodstockSource} onChange={v => set('broodstockSource', v)} />
            </Field>
            <Field label="CAA licence number" hint="Defaults to the hatchery registration if left blank.">
              <input value={form.caaLicenseNo} onChange={e => set('caaLicenseNo', e.target.value)} placeholder="AP/CAA/…" />
            </Field>
          </div>
        </Card>

        <Card title="2 · Capacity & timeline">
          <div className="formgrid">
            <Field label="Total batch capacity (lakh PL)" required>
              <input type="number" step="0.5" min="0" value={form.totalCapacityLakh} onChange={e => set('totalCapacityLakh', e.target.value)} required />
            </Field>
            <Field label="Minimum order (lakh PL)" required>
              <input type="number" step="0.5" min="0" value={form.minOrderLakh} onChange={e => set('minOrderLakh', e.target.value)} required />
            </Field>
            <Field label="Harvest-ready date" required hint="The desk matches farmers whose stocking date falls within two days before this.">
              <input type="date" value={form.readyDate} onChange={e => set('readyDate', e.target.value)} required />
            </Field>
            <Field label="PL stage on the ready date" required>
              <Select options={PL_STAGES} value={form.plStage} onChange={v => set('plStage', v)} />
            </Field>
          </div>
          {editing && existing.data?.batch?.bookedLakh > 0 && (
            <div className="hint">{existing.data.batch.bookedLakh} lakh PL is already blocked against bookings — capacity cannot be reduced below that.</div>
          )}
        </Card>

        <Card title="3 · Water & quality parameters">
          <div className="formgrid">
            <Field label="Acclimated salinity (ppt)" hint="The desk matches this against the farmer's pond salinity.">
              <input type="number" step="0.5" value={form.salinityPpt} onChange={e => set('salinityPpt', e.target.value)} />
            </Field>
            <Field label="Stress test survival (%)" hint="1-hour osmotic or temperature shock test.">
              <input type="number" step="0.1" min="0" max="100" value={form.stressTestPct} onChange={e => set('stressTestPct', e.target.value)} />
            </Field>
            <Field label="Size uniformity — CV (%)">
              <input type="number" step="0.1" value={form.sizeCvPct} onChange={e => set('sizeCvPct', e.target.value)} />
            </Field>
            <Field label="Feed / nursery protocol" wide>
              <input value={form.feedProtocol} onChange={e => set('feedProtocol', e.target.value)} placeholder="Artemia fraction A, 6 feeds/day" />
            </Field>
          </div>

          <div className="section-rule" />
          <div className="flabel">PCR screening</div>
          <div className="formgrid">
            <Field label="Laboratory">
              <input value={form.pcrLabName} onChange={e => set('pcrLabName', e.target.value)} />
            </Field>
            <Field label="Screened on">
              <input type="date" value={form.pcrScreenDate} onChange={e => set('pcrScreenDate', e.target.value)} />
            </Field>
            <Field label="WSSV"><Select options={RESULTS} value={form.pcrWssv} onChange={v => set('pcrWssv', v)} /></Field>
            <Field label="EHP"><Select options={RESULTS} value={form.pcrEhp} onChange={v => set('pcrEhp', v)} /></Field>
            <Field label="IHHNV"><Select options={RESULTS} value={form.pcrIhhnv} onChange={v => set('pcrIhhnv', v)} /></Field>
            <Field
              label="Certificate (PDF or image)"
              required={!existing.data?.batch?.pcr?.reportPath}
              hint="Mandatory before the batch can be published."
            >
              <input ref={pcrRef} type="file" accept=".pdf,image/*" onChange={e => setPcrFile(e.target.files?.[0] || null)} />
              {existing.data?.batch?.pcr?.reportPath && !pcrFile && (
                <div className="hint">
                  On file: <a href={existing.data.batch.pcr.reportPath} target="_blank" rel="noreferrer">view current certificate</a>
                </div>
              )}
            </Field>
          </div>
        </Card>

        <Card title="4 · Pricing & offers">
          <p className="muted small" style={{ marginTop: 0 }}>
            Visible to the platform operator only. The desk quotes it to farmers over the phone.
          </p>
          <div className="formgrid">
            <Field label="Base price per 1,000 PL (₹)" required>
              <input type="number" step="1" min="0" value={form.basePricePer1000} onChange={e => set('basePricePer1000', e.target.value)} required />
            </Field>
            <Field label="Offer type">
              <Select options={OFFER_TYPES} value={form.offerType} onChange={v => set('offerType', v)} />
            </Field>
            {form.offerType !== 'none' && <>
              <Field label={form.offerType === 'flat_off' ? 'Amount off per 1,000 PL (₹)' : 'Offer value (%)'} required>
                <input type="number" step="0.1" value={form.offerValue} onChange={e => set('offerValue', e.target.value)} required />
              </Field>
              <Field label="Applies above (lakh PL)">
                <input type="number" step="0.5" value={form.offerMinQtyLakh} onChange={e => set('offerMinQtyLakh', e.target.value)} />
              </Field>
              <Field label="Valid from"><input type="date" value={form.offerStart} onChange={e => set('offerStart', e.target.value)} /></Field>
              <Field label="Valid to"><input type="date" value={form.offerEnd} onChange={e => set('offerEnd', e.target.value)} /></Field>
              <Field label="Offer wording" wide hint="What the operator reads out, e.g. “Free 5% extra seed on orders above 10 lakh PL”.">
                <input value={form.offerDetails} onChange={e => set('offerDetails', e.target.value)} />
              </Field>
            </>}
            <Field label="Promotional note / banner" wide>
              <input value={form.promoNote} onChange={e => set('promoNote', e.target.value)} placeholder="High-salinity acclimated batch ready" />
            </Field>
          </div>
          {Number(form.totalCapacityLakh) > 0 && Number(form.basePricePer1000) > 0 && (
            <div className="hint">
              Full batch at base price ≈ <b>{inr(batchValue)}</b> ({qtyThousands.toLocaleString('en-IN')} × 1,000 PL)
            </div>
          )}
        </Card>

        <Card title="5 · Tank photos & clips">
          <p className="muted small" style={{ marginTop: 0 }}>
            Up to 5 photos and 2 clips (30 seconds each). The operator opens these on the call to show
            gut fullness, activity and tank condition. They are not broadcast to farmers.
          </p>
          <Field label="Add photos or video" hint="JPG, PNG, WEBP, MP4 or WEBM — up to 30 MB per file.">
            <input
              ref={mediaRef}
              type="file"
              multiple
              accept="image/*,video/mp4,video/webm"
              onChange={e => setMediaFiles(Array.from(e.target.files || []))}
            />
          </Field>
          {mediaFiles.length > 0 && (
            <div className="hint">{mediaFiles.length} file(s) will be attached when you save.</div>
          )}
          {editing && existing.data?.batch?.media?.length > 0 && (
            <>
              <div className="divider" />
              <div className="media-strip">
                {existing.data.batch.media.map(m => (
                  <figure key={m._id}>
                    {m.kind === 'video' ? <video src={m.path} controls /> : <img src={m.path} alt={m.caption || ''} />}
                    <figcaption>{m.caption || m.kind}</figcaption>
                  </figure>
                ))}
              </div>
              <div className="hint">Manage existing media from the batch page.</div>
            </>
          )}
        </Card>

        <div className="btnrow">
          <SubmitButton busy={busy} type="submit">
            {editing ? 'Save changes' : 'Save batch as draft'}
          </SubmitButton>
          <Link className="btn" to={editing ? `/hatchery/batches/${id}${scope.link}` : `/hatchery${scope.link}`}>Cancel</Link>
          {!editing && <span className="muted small">A new batch is saved as a draft — publish it from the batch page when you are ready.</span>}
        </div>
      </form>
    </Page>
  )
}
